/**
 * `Search: SearchApi` — binary searches over sorted typed arrays and id arrays (W1-01 core-time-math).
 * Plain loops with no allocation. NaN elements never compare below a target (they sort as "greater than
 * everything"); a NaN target finds the start of the range.
 */

import type { SearchApi } from './types';

function clampLo(len: number, lo: number | undefined): number {
  return lo === undefined ? 0 : Math.max(0, Math.min(lo, len));
}

function clampHi(len: number, l: number, hi: number | undefined): number {
  return hi === undefined ? len : Math.max(l, Math.min(hi, len));
}

export const Search: SearchApi = {
  lowerBound(arr, x, lo, hi) {
    let l = clampLo(arr.length, lo);
    let h = clampHi(arr.length, l, hi);
    while (l < h) {
      const mid = (l + h) >>> 1;
      if ((arr[mid] ?? Number.NaN) < x) l = mid + 1;
      else h = mid;
    }
    return l;
  },
  upperBound(arr, x, lo, hi) {
    let l = clampLo(arr.length, lo);
    let h = clampHi(arr.length, l, hi);
    while (l < h) {
      const mid = (l + h) >>> 1;
      if ((arr[mid] ?? Number.NaN) <= x) l = mid + 1;
      else h = mid;
    }
    return l;
  },
  lowerBoundBy(arr, x, key) {
    let l = 0;
    let h = arr.length;
    while (l < h) {
      const mid = (l + h) >>> 1;
      const item = arr[mid];
      if (item !== undefined && key(item) < x) l = mid + 1;
      else h = mid;
    }
    return l;
  },
};

/** First index i with key(arr[i]) > x (arr sorted ascending by key); arr.length when none. */
export function upperBoundBy<T>(arr: readonly T[], x: number, key: (item: T) => number): number {
  let l = 0;
  let h = arr.length;
  while (l < h) {
    const mid = (l + h) >>> 1;
    const item = arr[mid];
    if (item !== undefined && key(item) <= x) l = mid + 1;
    else h = mid;
  }
  return l;
}

/** Index of the last element <= x in a sorted array, or -1 when every element is greater. */
export function lastAtOrBefore(arr: ArrayLike<number>, x: number): number {
  return Search.upperBound(arr, x) - 1;
}

/** Generic lower bound with a comparator (`cmp(item) < 0` means the item sorts before the target). */
export function lowerBoundCmp<T>(arr: readonly T[], cmp: (item: T) => number): number {
  let l = 0;
  let h = arr.length;
  while (l < h) {
    const mid = (l + h) >>> 1;
    const item = arr[mid];
    if (item !== undefined && cmp(item) < 0) l = mid + 1;
    else h = mid;
  }
  return l;
}
